import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Notification, User, COLLECTION_NAMES } from '../types';

/**
 * Hook for managing Notifications data with Firestore
 * Replaces Supabase queries with Firestore v9+ modular API
 * 
 * Note: Notifications are stored in user subcollections: users/{userId}/Notifications/{notificationId}
 */
export function useNotifications(userId: string | undefined, selectedUserId?: string | null) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { eosUsers = [] } = useAuth();
  const [loading, setLoading] = useState(true);

  // Load notifications with real-time listener and filtering
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Build the query for the user's notifications subcollection
    const notificationsQuery = query(
      collection(db, COLLECTION_NAMES.USERS, userId, COLLECTION_NAMES.NOTIFICATIONS),
      orderBy('timestamp', 'desc')
    );

    // Note: Firestore doesn't support filtering on subcollections with additional where clauses
    // in the same way as top-level collections. We'll filter client-side for triggeredByUserId
    // If you need server-side filtering, consider storing notifications in a flat collection
    // with explicit user_id and triggeredByUserId fields.

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notificationsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp || Timestamp.now(),
            createdAt: data.createdAt || data.timestamp || Timestamp.now(),
            read: data.read || false,
            isRead: data.isRead !== undefined ? data.isRead : (data.read ? true : false),
          } as Notification;
        });

        // Filter by triggeredByUserId if selected
        let filtered = notificationsData;
        if (selectedUserId) {
          filtered = notificationsData.filter(
            (notification) => notification.triggeredByUserId === selectedUserId
          );
        }

        setNotifications(filtered);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading notifications:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, selectedUserId]);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!userId) return;

    try {
      const notificationRef = doc(
        db,
        COLLECTION_NAMES.USERS,
        userId,
        COLLECTION_NAMES.NOTIFICATIONS,
        notificationId
      );
      await updateDoc(notificationRef, {
        read: Timestamp.now(),
        isRead: true,
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }, [userId]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;

    try {
      const unreadNotifications = notifications.filter((n) => !n.isRead && !n.read);
      const updatePromises = unreadNotifications.map((notification) => {
        const notificationRef = doc(
          db,
          COLLECTION_NAMES.USERS,
          userId,
          COLLECTION_NAMES.NOTIFICATIONS,
          notification.id
        );
        return updateDoc(notificationRef, {
          read: Timestamp.now(),
          isRead: true,
        });
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }, [userId, notifications]);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!userId) return;

    try {
      const notificationRef = doc(
        db,
        COLLECTION_NAMES.USERS,
        userId,
        COLLECTION_NAMES.NOTIFICATIONS,
        notificationId
      );
      await deleteDoc(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }, [userId]);

  const unreadCount = notifications.filter((n) => !n.isRead && !n.read).length;

  return {
    notifications,
    users: eosUsers,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}
